import { useTranslations } from '@vocab/react';
import { Helmet } from 'react-helmet';
import { useFormContext } from 'react-hook-form';

import {
  type Question,
  RoleRequirementsForm,
} from 'src/components/RoleRequirementsForm/RoleRequirementsForm';
import { useConfig } from 'src/context/config';
import { StepId, useStepsCompleted } from 'src/context/use-steps-completed';
import { useAllQuestions } from 'src/hooks/useAllQuestions/useAllQuestions';
import { BottomNav } from 'src/ui/BottomNav/BottomNav';
import PageLoader from 'src/ui/PageLoader/PageLoader';

import translations from './.vocab';
import { useTracking } from './hooks/useTracking';

interface Props {
  onNext: () => void;
  onPrev: () => void;
}

export function RoleRequirements({ onPrev, onNext }: Props) {
  useTracking();
  const { config } = useConfig();
  const { t } = useTranslations(translations);
  const { data, loading } = useAllQuestions();

  const { setCompleted } = useStepsCompleted();

  const questions = data.filter(
    (q) =>
      q.__typename === 'SingleChoiceQuestion' ||
      q.__typename === 'MultipleChoiceQuestion' ||
      q.__typename === 'FreeTextQuestion',
  ) as Question[];

  const { handleSubmit, reset } = useFormContext();

  function handleNext() {
    setCompleted(StepId.ROLE_REQUIREMENTS_ID);
    // reset isDirty state
    reset(undefined, { keepValues: true, keepDirty: false });
    onNext?.();
  }

  function handlePrev() {
    // reset isDirty state
    reset(undefined, { keepValues: true, keepDirty: false });
    onPrev?.();
  }

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <Helmet>
        <title>{`${t('Answer employer questions')} | ${config.BRAND}`}</title>
      </Helmet>
      <RoleRequirementsForm
        questions={questions}
        actions={
          <BottomNav
            onNext={handleSubmit(handleNext)}
            onPrevious={handleSubmit(handlePrev)}
          />
        }
      />
    </>
  );
}
